import { ArrowPathIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import {
  CALENDAR_OUTLINE_BUTTON,
  CALENDAR_PRIMARY_BUTTON,
} from '~/features/shared/calendar/controlStyles';

interface BulkDateToolbarProps {
  busy: boolean;
  count: number;
  /** Selected dates that fall outside the currently visible month/week. */
  offScreenCount?: number;
  onClear: () => void;
  onClearSelection: () => void;
  onMarkAvailable: () => void;
  onMarkUnavailable: () => void;
  onOpenRecurring: () => void;
  onOpenEditor: () => void;
  editorDisabled: boolean;
  readOnly: boolean;
  recurringDisabled: boolean;
}

export const BulkDateToolbar = ({
  busy,
  count,
  offScreenCount = 0,
  onClear,
  onClearSelection,
  onMarkAvailable,
  onMarkUnavailable,
  onOpenRecurring,
  onOpenEditor,
  editorDisabled,
  readOnly,
  recurringDisabled,
}: BulkDateToolbarProps) => (
  <div
    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-grey-900 bg-white p-3 shadow-sm"
    data-cy="provider-calendar-bulk-toolbar"
  >
    <div className="flex w-full flex-wrap items-center justify-between gap-2 sm:w-auto sm:justify-start sm:gap-3">
      <p
        className="body-text text-grey-400"
        aria-live="polite"
        data-cy="provider-calendar-selection-count"
      >
        {count === 0
          ? 'Select one or more future dates'
          : `${count} date${count === 1 ? '' : 's'} selected${
              offScreenCount > 0 ? ` (${offScreenCount} outside this view)` : ''
            }`}
      </p>
      <button
        className={classNames(
          'body-text-sb text-primary-600 hover:underline disabled:cursor-not-allowed disabled:text-grey-700 disabled:no-underline',
          count === 0 && 'hidden sm:inline-block'
        )}
        data-cy="provider-calendar-clear-selection"
        disabled={count === 0}
        onClick={onClearSelection}
        type="button"
      >
        Clear selection
      </button>
    </div>
    <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap">
      <button
        className={classNames(CALENDAR_OUTLINE_BUTTON, 'min-w-0', count === 0 && 'max-sm:hidden')}
        data-cy="provider-calendar-edit-open"
        disabled={editorDisabled || count === 0}
        onClick={onOpenEditor}
        type="button"
      >
        {readOnly ? 'View availability' : 'Edit availability'}
      </button>
      <button
        className={classNames(CALENDAR_PRIMARY_BUTTON, 'min-w-0', count === 0 && 'max-sm:hidden')}
        data-cy="provider-calendar-mark-available"
        disabled={busy || count === 0}
        onClick={onMarkAvailable}
        type="button"
      >
        Mark available
      </button>
      <button
        className={classNames(CALENDAR_OUTLINE_BUTTON, 'min-w-0', count === 0 && 'max-sm:hidden')}
        data-cy="provider-calendar-mark-unavailable"
        disabled={busy || count === 0}
        onClick={onMarkUnavailable}
        type="button"
      >
        Mark unavailable
      </button>
      <button
        className={classNames(CALENDAR_OUTLINE_BUTTON, 'min-w-0', count === 0 && 'max-sm:hidden')}
        data-cy="provider-calendar-clear"
        disabled={busy || count === 0}
        onClick={onClear}
        type="button"
      >
        Clear editable
      </button>
      <button
        className={`${CALENDAR_OUTLINE_BUTTON} col-span-2`}
        data-cy="provider-calendar-recurring-open"
        disabled={recurringDisabled}
        onClick={onOpenRecurring}
        type="button"
      >
        <ArrowPathIcon className="size-4" /> Recurring availability
      </button>
    </div>
  </div>
);
